import { Link, useRouter } from "@tanstack/react-router";
import { BookOpen, MessageCircle, Settings } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { FeedbackModal } from "./ui/feedback-modal";
import { Logo } from "./ui/Logo";

interface HeaderProps {
    variant?: "auth" | "main";
    title?: string;
}

function UserActions() {
    const { user, loading, signOut } = useAuth();
    const router = useRouter();
    const currentPath = router.state.location.pathname;
    const isOnAuthPage = currentPath === "/sign-in" || currentPath === "/sign-up";
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

    // Show loading placeholder while auth state is being determined
    if (loading) {
        return (
            <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-3">
                    <div className="h-10 w-16 bg-muted animate-pulse rounded"></div>
                    <div className="h-10 w-20 bg-muted animate-pulse rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-3">
                {user ? (
                    <>
                        {/* Mobile feedback button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsFeedbackModalOpen(true)}
                            className="md:hidden h-10 w-10 p-0 hover:text-primary hover:bg-primary/10"
                            title="Send Feedback"
                        >
                            <MessageCircle className="h-4 w-4" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="hidden md:flex h-10 w-10 p-0 hover:text-primary hover:bg-primary/10"
                        >
                            <Link to="/my-stories">
                                <BookOpen className="h-5 w-5" />
                            </Link>
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="hidden md:flex h-10 w-10 p-0 hover:text-primary hover:bg-primary/10"
                        >
                            <Link to="/settings">
                                <Settings className="h-5 w-5" />
                            </Link>
                        </Button>

                        {/* Desktop feedback button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsFeedbackModalOpen(true)}
                            className="hidden md:flex h-10 w-auto px-3 hover:text-primary hover:bg-primary/10"
                            title="Send Feedback"
                        >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            <span className="text-xs">Feedback</span>
                        </Button>

                        <Button variant="outline" onClick={() => signOut()} className="text-xs px-2 md:px-4 md:text-sm">
                            Sign Out
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="ghost"
                            asChild
                            className="hidden md:flex hover:text-primary hover:bg-primary/10"
                        >
                            <Link to="/sign-in" search={isOnAuthPage ? undefined : { redirectTo: currentPath }}>
                                Sign In
                            </Link>
                        </Button>
                        <Button asChild className="hidden md:flex">
                            <Link to="/sign-up">Sign Up</Link>
                        </Button>
                    </>
                )}
            </div>

            {isFeedbackModalOpen && <FeedbackModal onClose={() => setIsFeedbackModalOpen(false)} />}
        </>
    );
}

export function Header({ variant = "main", title }: HeaderProps) {
    if (variant === "auth") {
        return (
            <>
                {/* Mobile Header */}
                <header className="md:hidden sticky top-0 z-50 px-4 py-2 flex justify-end items-center border-b page-gradient">
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <UserActions />
                    </div>
                </header>

                {/* Desktop Header */}
                <header className="hidden md:flex sticky top-0 z-50 px-6 py-4 justify-end items-center border-b page-gradient md:bg-background">
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <UserActions />
                    </div>
                </header>
            </>
        );
    }

    return (
        <>
            {/* Mobile Header */}
            <header className="md:hidden sticky top-0 z-50 px-4 py-2 flex justify-between items-center border-b page-gradient">
                <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                    <div className="flex items-center">
                        <Logo size="lg" clickable />
                    </div>
                    <div className="w-px h-6 bg-border mx-1" />
                    {title && (
                        <h1 className="text-base font-semibold truncate min-w-0">
                            {title === "StoryDoodle" ? (
                                <>
                                    <span className="text-primary">Story</span>
                                    <span className="text-secondary">Doodle</span>
                                </>
                            ) : (
                                <span className="text-foreground">{title}</span>
                            )}
                        </h1>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <UserActions />
                </div>
            </header>

            {/* Desktop Header */}
            <header className="hidden md:flex sticky top-0 z-50 px-6 py-4 justify-between items-center border-b page-gradient md:bg-background">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center">
                        <Logo size="lg" clickable />
                    </div>
                    <div className="w-px h-6 bg-border mx-1" />
                    {title && (
                        <h1 className="text-2xl font-bold truncate min-w-0">
                            {title === "StoryDoodle" ? (
                                <>
                                    <span className="text-primary">Story</span>
                                    <span className="text-secondary">Doodle</span>
                                </>
                            ) : (
                                <span className="text-foreground">{title}</span>
                            )}
                        </h1>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <UserActions />
                </div>
            </header>
        </>
    );
}
